import React, { useEffect, useState, useCallback, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation, useNavigate } from 'react-router-dom';

import { Tooltip } from 'react-tooltip';
import dayjs from 'dayjs';
import customParseFormat from 'dayjs/plugin/customParseFormat';
import { selectUserData } from '../../../../../../../redux/auth/authSelector';
import { setBreadcrumbs } from '../../../../../../../redux/Admin/breadcrumbsSlice';
import {
  getParticularSessionDetails,
  clearParticularSessionDetails,
  enableCompanySession,
  enableToolAccess,
  enableSupportDocument,
  enableAssignment,
  getCompanySessionDetail,
  clearCompanySessionDetail
} from '../../../../../../../redux/CompanySession/companiesSessionSlice';
import {
  selectParticularSessionDetails,
  selectParticularSessionDetailsLoading,
  selectParticularSessionDetailsError,
  selectCompanySessionDetail,
  selectCompanySessionDetailLoading,
  selectCompanySessionDetailError
} from '../../../../../../../redux/CompanySession/companiesSessionSelector';
import ToolManagmentCard from '../../../../../../../common/element/Card/ToolManagmnetCard';
import Switcher from '../../../../../../../common/element/Switcher';
import CandidateToolResult from './CandidateToolsResult';
import SweetAlertService from '../../../../../../../common/SweetAlert/SweetAlert';
import ConfirmationModal from '../../../../../../../common/Common/ConfirmationModal';
import ThreeDotBouncingLoader from '../../../../../../../common/element/ButtonLoader/ThreeDotBouncingLoder';
import AssignmentResult from './AssignmentResult';
import { formatToolName } from '../../../../../../../common/Formatters.js/formatters';
import { resetsessionName, setsessionName } from '../../../../../../../redux/Admin/sessionViewSlice';
import { ROLES } from '../../../../../../../config/rolePermissions';

// Extend dayjs with custom parse format plugin (after all imports)
dayjs.extend(customParseFormat);

// ✅ Reusable Loading Spinner Component
const LoadingSpinner = () => {
  return (
    <><ThreeDotBouncingLoader /></>
  )
};

// ✅ Card Loader Component
const CardLoader = () => (
  <div className="flex items-center justify-center py-12">
    <div className="text-center">
      <LoadingSpinner size="lg" />
    </div>
  </div>
);

// ✅ Status Badge Component
const StatusBadge = ({ status }) => {
  const getStatusColor = (status) => {
    const colors = {
      completed: "text-green-600 bg-green-50",
      upcoming: "text-blue-600 bg-blue-50",
      ongoing: "text-yellow-600 bg-yellow-50",
      cancelled: "text-red-600 bg-red-50"
    };
    return colors[status?.toLowerCase()] || "text-gray-600 bg-gray-50";
  };

  return (
    <span className={`inline-block px-3 py-1 text-sm font-medium rounded-full capitalize ${getStatusColor(status)}`}>
      {status || 'N/A'}
    </span>
  );
};

// ✅ Toggle with Tooltip Component
const ToggleWithTooltip = ({
  id,
  checked,
  onChange,
  disabled,
  loading,
  tooltipContent
}) => (
  <div className="inline-flex items-center">
    {loading ? (
      <div className="w-10 h-5 flex items-center justify-center">
        <LoadingSpinner size="sm" />
      </div>
    ) : (
      <>
        <div
          data-tooltip-id={id}
          data-tooltip-content={tooltipContent}
        >
          <Switcher
            checked={checked}
            onChange={onChange}
            disabled={disabled}
          />
        </div>
        {tooltipContent && (
          <Tooltip
            id={id}
            place="top"
            className="!bg-white !text-black !text-sm !rounded-lg !px-3 !py-2 !z-50 !max-w-[250px] shadow-[0px_8px_16px_-2px_rgba(27,33,44,0.12)]"
          />
        )}
      </>
    )}
  </div>
);

// ✅ Check Icon SVG
const CheckIcon = () => (
  <svg width="18" height="18" viewBox="0 0 25 20" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M2.5 10.7297L8.68692 17L22.5 3" stroke="#027A48" strokeWidth="4.5" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

// ✅ Cross Icon SVG
const CrossIcon = () => (
  <svg width="16" height="16" viewBox="0 0 21 20" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M13.6844 10L19.8296 3.88889C20.7235 3 20.7235 1.66667 19.8296 0.777778C19.4944 0.333333 18.9358 0 18.3771 0C17.8184 0 17.2598 0.222222 16.8129 0.666667L10.5559 6.88889L4.41061 0.777778C3.51676 -0.111111 2.06425 -0.111111 1.28212 0.777778C0.835195 1.11111 0.5 1.66667 0.5 2.33333C0.5 3 0.723464 3.44444 1.17039 3.88889L7.31564 10L1.17039 16.1111C0.835195 16.5556 0.5 17.1111 0.5 17.7778C0.5 18.3333 0.723464 18.8889 1.17039 19.3333C1.61732 19.7778 2.17598 20 2.73464 20C3.2933 20 3.85196 19.7778 4.29888 19.3333L10.4441 13.2222L16.5894 19.3333C17.4832 20.2222 18.9358 20.2222 19.7179 19.3333C20.6117 18.4444 20.6117 17 19.7179 16.2222L13.6844 10Z" fill="#B42318" />
  </svg>
);

// ✅ View Icon SVG
const ViewIcon = () => (
  <svg width="19" height="14" viewBox="0 0 19 14" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M1.05388 7.26163C0.940389 7.08193 0.883645 6.99208 0.851879 6.8535C0.828019 6.7494 0.828019 6.58523 0.851879 6.48114C0.883645 6.34255 0.940389 6.2527 1.05388 6.073C1.99172 4.58802 4.78328 0.833984 9.03745 0.833984C13.2916 0.833984 16.0832 4.58802 17.021 6.073C17.1345 6.2527 17.1912 6.34255 17.223 6.48114C17.2469 6.58523 17.2469 6.7494 17.223 6.8535C17.1912 6.99208 17.1345 7.08193 17.021 7.26163C16.0832 8.74662 13.2916 12.5007 9.03745 12.5007C4.78328 12.5007 1.99172 8.74662 1.05388 7.26163Z" stroke="#475467" strokeWidth="1.66667" strokeLinecap="round" strokeLinejoin="round" />
    <path d="M9.03745 9.16732C10.4182 9.16732 11.5374 8.04803 11.5374 6.66732C11.5374 5.28661 10.4182 4.16732 9.03745 4.16732C7.65673 4.16732 6.53745 5.28661 6.53745 6.66732C6.53745 8.04803 7.65673 9.16732 9.03745 9.16732Z" stroke="#475467" strokeWidth="1.66667" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

// ✅ Info Icon SVG
const InfoIcon = () => (
  <svg width="12" height="12" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M10 0C4.477 0 0 4.477 0 10C0 15.523 4.477 20 10 20C15.523 20 20 15.523 20 10C20 4.477 15.523 0 10 0ZM10 4.5C10.828 4.5 11.5 5.172 11.5 6C11.5 6.828 10.828 7.5 10 7.5C9.172 7.5 8.5 6.828 8.5 6C8.5 5.172 9.172 4.5 10 4.5ZM12 15H8V13H9V10H8V8H11V13H12V15Z" fill="white" />
  </svg>
);

const SessionView = ({ companyData }) => {
  const dispatch = useDispatch();
  const location = useLocation();
  const navigate = useNavigate();
 const userData = useSelector(selectUserData);
  const isAdmin = userData?.role_name === ROLES.ADMIN;
  // URL Params
  const searchParams = new URLSearchParams(location.search);
  const SessionId = searchParams.get("SessionId");
  const sessionTab = searchParams.get("sessionTab");
  const tab = searchParams.get("tab");
  const companyId = searchParams.get("companyId");
  const storedSessionName = useSelector((state) => state.sessionView?.sessionName);

  // Redux State - Particular Session Details
  const sessionDetails = useSelector(selectParticularSessionDetails);
  const loading = useSelector(selectParticularSessionDetailsLoading);
  const error = useSelector(selectParticularSessionDetailsError);

  // ✅ Redux State - Company Session Detail (for candidates)
  const companySessionDetail = useSelector(selectCompanySessionDetail);
  const candidatesLoading = useSelector(selectCompanySessionDetailLoading);
  const candidatesError = useSelector(selectCompanySessionDetailError);

  // persisted company name (set by parent View component)
  const storedCompanyName = useSelector((state) => state.companyView?.companyName);

  // Loading States
  const [loadingStates, setLoadingStates] = useState({
    tool: false,
    docs: false,
    assignment: false,
    status: false
  });

  // Modal State
  const [dialogOpen, setDialogOpen] = useState(false);
  const [selectedCandidate, setSelectedCandidate] = useState(null);
  const [dialogOpenAssignment, setDialogOpenAssignment] = useState(false);
  const [selectedCandidateAssignment, setSelectedCandidateAssignment] = useState(null);
  const [confirmationModal, setConfirmationModal] = useState({
    isOpen: false,
    title: "",
    message: "",
    action: null, // Will store the function to call on "Yes"
  });
  const [assignmentName, setAssignmentName] = useState(null);

  // ✅ Check Status Constraints (Cancelled/Completed/Inactive)
  const isCompanyActive = companyData?.company_isActive;
  const sessionStatus = sessionDetails?.status?.toLowerCase();
  const isSessionCancelled = sessionStatus === 'cancelled';
  const isSessionCompleted = sessionStatus === 'completed';

  // ✅ Update disabled logic to include company inactive check
  const areActionsDisabled = isSessionCancelled || isSessionCompleted || !isCompanyActive;

  // ✅ Extract all employees from batches (memoized)
  const candidates = useMemo(() => {
    if (!companySessionDetail?.batches) return [];

    const allEmployees = [];
    companySessionDetail.batches.forEach((batch) => {
      if (batch.employees && Array.isArray(batch.employees)) {
        batch.employees.forEach((employee) => {
          allEmployees.push({
            id: employee.employee_id,
            batch_employee_id: employee.batch_employee_id,
            name: employee.employee_name?.trim() || 'N/A',
            email: employee.email || 'N/A',
            batch_id: batch.batch_id,
            batch_name: batch.batch_name
          });
        });
      }
    });

    return allEmployees;
  }, [companySessionDetail]);

  // ✅ Helper: Set loading state
  const setLoading = (key, value) => {
    setLoadingStates(prev => ({ ...prev, [key]: value }));
  };

  // ✅ Helper: Refresh session details
  const refreshSessionDetails = useCallback(() => {
    if (companyId && SessionId) {
      dispatch(getParticularSessionDetails({
        companyId: parseInt(companyId),
        companySessionId: parseInt(SessionId)
      }));
    }
  }, [companyId, SessionId, dispatch]);

  // ✅ Helper: Fetch candidates (company session detail)
  const fetchCandidates = useCallback(() => {
    if (companyId && SessionId) {
      dispatch(getCompanySessionDetail({
        companyId: parseInt(companyId),
        sessionId: parseInt(SessionId)
      }));
    }
  }, [companyId, SessionId, dispatch]);

  // ✅ Helper: Format date using dayjs
  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    const parsed = dayjs(dateString);
    if (!parsed.isValid()) return 'N/A';
    return parsed.format('DD/MM/YYYY');
  };

  // ✅ Helper: Format time to AM/PM using dayjs
  const formatTime = (timeString) => {
    if (!timeString) return 'N/A';

    const formats = ['HH:mm:ss', 'HH:mm', 'h:mm A', 'h:mm a', 'H:mm:ss', 'H:mm'];

    for (const format of formats) {
      const parsed = dayjs(timeString, format);
      if (parsed.isValid()) {
        return parsed.format('h:mm A');
      }
    }

    return timeString;
  };

  // ✅ Helper: Get toggle tooltip
  const getToggleTooltip = (type) => {
    if (!isCompanyActive) {
      return 'Company is inactive, actions are disabled';
    }

    if (isSessionCancelled) {
      return 'Session is cancelled, actions are disabled';
    }
    if (isSessionCompleted) {
      return 'Session is completed, actions are disabled';
    }

    if (type === 'tool') {
      if (sessionDetails?.tool_enabled) {
        return 'Tool already sent';
      }
      return 'Send tool';
    }
    if (type === 'docs') {
      if (sessionDetails?.supported_doc_enabled) {
        return 'Supported document already sent';
      }
      return 'Send the supported document';
    }
    if (type === 'assignment') {
      if (sessionDetails?.assignment_enabled) {
        return 'Assignment already sent';
      }
      return 'Send assignment';
    }
    return '';
  };

  // ✅ Helper function to open confirmation modal
  const openConfirmationModal = (title, message, onConfirm) => {
    setConfirmationModal({
      isOpen: true,
      title,
      message,
      action: onConfirm,
    });
  };

  // ✅ Handle Confirmation Modal Yes
  const handleConfirmationYes = async () => {
    if (confirmationModal.action) {
      await confirmationModal.action();
    }
    setConfirmationModal({
      isOpen: false,
      title: "",
      message: "",
      action: null,
    });
  };

  // ✅ Handle Confirmation Modal No
  const handleConfirmationNo = () => {
    setConfirmationModal({
      isOpen: false,
      title: "",
      message: "",
      action: null,
    });
  };

  // ✅ API: Handle Tool Toggle
  const handleToolToggle = async (checked) => {
    if (!companyId || !sessionDetails || !isCompanyActive) return;

    if (!checked && sessionDetails.tool_enabled) {
      SweetAlertService.showWarning({
        title: 'Action Not Allowed',
        message: 'Tool access cannot be disabled once enabled.'
      });
      return;
    }

    openConfirmationModal(
      'Send Tool?',
      'Are you sure you want to send the tool for this session?',
      async () => {
        setLoading('tool', true);

        try {
          const toolData = {
            company_session_id: sessionDetails.company_session_id || parseInt(SessionId),
            tool_enabled: checked,
            tool_id: sessionDetails.tools?.[0]?.tool_id,
            batch_ids: sessionDetails.batches?.map(b => b.batch_id) || [],
            is_locked: !checked
          };

          const result = await dispatch(enableToolAccess({ companyId: parseInt(companyId), toolData }));

          if (enableToolAccess.fulfilled.match(result)) {
            SweetAlertService.showSuccess({ title: 'Success!', message: 'Tool access enabled successfully.' });
            refreshSessionDetails();
          } else {
            throw new Error(result.payload?.message || 'Failed to enable tool access');
          }
        } catch (err) {
          SweetAlertService.showError({ title: 'Error!', message: err.message });
        } finally {
          setLoading('tool', false);
        }
      }
    );
  };

  // ✅ API: Handle Supported Documents Toggle
  const handleDocsToggle = async (checked) => {
    if (!companyId || !sessionDetails || !isCompanyActive) return;

    if (!checked && sessionDetails.supported_doc_enabled) {
      SweetAlertService.showWarning({
        title: 'Action Not Allowed',
        message: 'Support document access cannot be disabled once enabled.'
      });
      return;
    }

    openConfirmationModal(
      'Send Supported Documents?',
      'Are you sure you want to send the supported documents for this session?',
      async () => {
        setLoading('docs', true);

        try {
          const documentData = {
            company_session_id: sessionDetails.company_session_id || parseInt(SessionId),
            supported_doc_enabled: checked,
            supported_doc_id: sessionDetails.supported_documents?.[0]?.supported_doc_id,
            batch_ids: sessionDetails.batches?.map(b => b.batch_id) || []
          };

          const result = await dispatch(enableSupportDocument({ companyId: parseInt(companyId), documentData }));

          if (enableSupportDocument.fulfilled.match(result)) {
            SweetAlertService.showSuccess({ title: 'Success!', message: 'Support document enabled successfully.' });
            refreshSessionDetails();
          } else {
            throw new Error(result.payload?.message || 'Failed to enable support document');
          }
        } catch (err) {
          SweetAlertService.showError({ title: 'Error!', message: err.message });
        } finally {
          setLoading('docs', false);
        }
      }
    );
  };

  // ✅ API: Handle Assignment Toggle
  const handleAssignmentToggle = async (checked) => {
    if (!companyId || !sessionDetails || !isCompanyActive) return;

    if (!checked && sessionDetails.assignment_enabled) {
      SweetAlertService.showWarning({
        title: 'Already Sent',
        message: 'Assignment has already been sent and cannot be resent.'
      });
      return;
    }

    openConfirmationModal(
      'Send Assignment?',
      'Are you sure you want to send the assignment for this session?',
      async () => {
        setLoading('assignment', true);

        try {
          const assignmentData = {
            company_session_id: sessionDetails.company_session_id || parseInt(SessionId),
            assignment_enabled: true,
            assignment_id: sessionDetails.assignments?.[0]?.assignment_id || null,
            batch_ids: sessionDetails.batches?.map(b => b.batch_id) || []
          };

          const result = await dispatch(enableAssignment({ companyId: parseInt(companyId), assignmentData }));

          if (enableAssignment.fulfilled.match(result)) {
            SweetAlertService.showSuccess({ title: 'Success!', message: 'Assignment sent successfully.' });
            refreshSessionDetails();
          } else {
            throw new Error(result.payload?.message || 'Failed to send assignment');
          }
        } catch (err) {
          SweetAlertService.showError({ title: 'Error!', message: err.message });
        } finally {
          setLoading('assignment', false);
        }
      }
    );
  };

  // ✅ API: Handle Session Status (Complete/Cancel)
  const handleAssignStatus = async (status) => {
    if (!companyId || !sessionDetails || !isCompanyActive) return;

    setLoading('status', true);

    try {
      const body = {
        company_session_id: sessionDetails.company_session_id || parseInt(SessionId),
        assign_status: status,
        ...(status === true && { mark_completed: true }),
      };

      const result = await dispatch(enableCompanySession({ companyId: parseInt(companyId), body }));

      if (enableCompanySession.fulfilled.match(result)) {
        SweetAlertService.showSuccess({
          message: status ? 'Session marked as completed.' : 'Session cancelled successfully.'
        });
        refreshSessionDetails();
      } else {
        throw new Error(result.payload?.message || 'Failed to update session status');
      }
    } catch (err) {
      SweetAlertService.showError({ message: err.message });
    } finally {
      setLoading('status', false);
    }
  };

  // ✅ Fetch session details and candidates on mount
  useEffect(() => {
    refreshSessionDetails();
    fetchCandidates();

    return () => {
      dispatch(clearParticularSessionDetails());
      dispatch(clearCompanySessionDetail());
    };
  }, [refreshSessionDetails, fetchCandidates, dispatch]);

  // reset stored session name when leaving this view (but not on full page reload)
  useEffect(() => {
    const isReload = { current: false };
    const onBeforeUnload = () => { isReload.current = true; };
    window.addEventListener('beforeunload', onBeforeUnload);
    return () => {
      window.removeEventListener('beforeunload', onBeforeUnload);
      if (!isReload.current) {
        dispatch(resetsessionName());
      }
    };
  }, [dispatch]);

  // If session details arrive from the API, persist its name
  useEffect(() => {
    if (sessionDetails && (sessionDetails.session_name || sessionDetails.sessionName)) {
      const name = sessionDetails.session_name || sessionDetails.sessionName;
      dispatch(setsessionName(name));
    }
  }, [sessionDetails, dispatch]);

  // ✅ Set breadcrumbs
  useEffect(() => {
    const companyLabel = companyData?.company_name || storedCompanyName || 'View Company';
    const sessionLabel = storedSessionName || 'Session View';
    const crumbs = [
      { label: "Companies", url: "/companies", home: "/companies" },
      { label: companyLabel, url: `/companies/companyview?companyId=${companyId}&tab=${tab}` },
    ];

    if (SessionId) {
      crumbs.push({
        label: sessionLabel,
        url: `/companies/companyview?companyId=${companyId}&tab=${tab}&SessionId=${SessionId}&sessionTab=${sessionTab}`,
      });
    }

    dispatch(setBreadcrumbs(crumbs));
  }, [companyId, tab, SessionId, sessionTab, dispatch, storedSessionName, companyData]);

  // ✅ Render Status Buttons
  const renderStatusButtons = () => {
    if (loading || loadingStates.status) {
      return <LoadingSpinner size="sm" />;
    }

    // Cancelled status
    if (isSessionCancelled) {
      const cancelledAt = sessionDetails?.status_changed_at
        ? dayjs(sessionDetails.status_changed_at).format('DD/MM/YYYY')
        : null;

      return (
        <div className="flex items-center gap-2">
          <button
            disabled
            className="px-4 py-2 rounded-full bg-red-50 text-red-500 not-italic font-bold text-base leading-[150%] cursor-not-allowed"
          >
            Cancelled
          </button>

          {cancelledAt && (
            <>
              <div
                data-tooltip-id="status-info-tooltip"
                data-tooltip-content={`You canceled the session already on ${cancelledAt}`}
                className="flex items-center justify-center w-7 h-7 rounded-[8px] bg-[#FF8E1C] cursor-pointer flex-shrink-0"
              >
                <svg width="6" height="14" viewBox="0 0 6 14" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M0.546875 11.6H1.09375V7.64999H0.546875C0.244836 7.64999 0 7.40515 0 7.10311V5.79688C0 5.49484 0.244836 5.25 0.546875 5.25H3.60938C3.91141 5.25 4.15625 5.49484 4.15625 5.79688V11.6H4.70312C5.00516 11.6 5.25 11.8448 5.25 12.1469V13.4531C5.25 13.7552 5.00516 14 4.70312 14H0.546875C0.244836 14 0 13.7552 0 13.4531V12.1469C0 11.8448 0.244836 11.6 0.546875 11.6ZM2.625 0C1.53768 0 0.65625 0.881426 0.65625 1.96875C0.65625 3.05607 1.53768 3.9375 2.625 3.9375C3.71232 3.9375 4.59375 3.05607 4.59375 1.96875C4.59375 0.881426 3.7123 0 2.625 0Z" fill="white" />
                </svg>

              </div>
              <Tooltip
                id="status-info-tooltip"
                place="top"
                className="!bg-gray-900 !text-white !text-xs !rounded-lg !px-3 !py-2 !z-50 !max-w-[280px]"
              />
            </>
          )}
        </div>
      );
    }

    // Completed status
    if (isSessionCompleted) {
      const completedAt = sessionDetails?.status_changed_at
        ? dayjs(sessionDetails.status_changed_at).format('DD/MM/YYYY')
        : null;

      return (
        <div className="flex items-center gap-2">
          <button
            disabled
            className="px-4 py-2 rounded-full bg-[#ECFDF3] text-[#027A48] not-italic font-bold text-base leading-[150%] cursor-not-allowed"
          >
            Completed
          </button>

          {completedAt && (
            <>
              <div
                data-tooltip-id="status-info-tooltip"
                data-tooltip-content={`You have sent the session already on ${completedAt}`}
                className="flex items-center justify-center w-7 h-7 bg-[#FF8E1C] cursor-pointer flex-shrink-0 rounded-[8px]"
              >
                <svg width="6" height="14" viewBox="0 0 6 14" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M0.546875 11.6H1.09375V7.64999H0.546875C0.244836 7.64999 0 7.40515 0 7.10311V5.79688C0 5.49484 0.244836 5.25 0.546875 5.25H3.60938C3.91141 5.25 4.15625 5.49484 4.15625 5.79688V11.6H4.70312C5.00516 11.6 5.25 11.8448 5.25 12.1469V13.4531C5.25 13.7552 5.00516 14 4.70312 14H0.546875C0.244836 14 0 13.7552 0 13.4531V12.1469C0 11.8448 0.244836 11.6 0.546875 11.6ZM2.625 0C1.53768 0 0.65625 0.881426 0.65625 1.96875C0.65625 3.05607 1.53768 3.9375 2.625 3.9375C3.71232 3.9375 4.59375 3.05607 4.59375 1.96875C4.59375 0.881426 3.7123 0 2.625 0Z" fill="white" />
                </svg>

              </div>
              <Tooltip
                id="status-info-tooltip"
                place="top"
                className="!bg-gray-900 !text-white !text-xs !rounded-lg !px-3 !py-2 !z-50 !max-w-[280px]"
              />
            </>
          )}
        </div>
      );
    }

    // Not sent - show action buttons
    return (
      <>
        {/* <div
          data-tooltip-id="status-tooltip"
          data-tooltip-content={!isCompanyActive ? "Company is inactive" : "Complete session"}
          className="inline-block"
        >
          <button
            onClick={() => {
              if (!isCompanyActive) return;
              openConfirmationModal(
                'Complete Session?',
                'Are you sure you want to mark this session as completed?',
                () => handleAssignStatus(true)
              );
            }}
            disabled={!isCompanyActive}
            className={`text-green-600 ${!isCompanyActive ? 'opacity-50 cursor-not-allowed' : 'hover:text-green-700'} transition-colors`}
            aria-label="Complete session"
          >
            <CheckIcon />
          </button>
        </div>

        <div
          data-tooltip-id="status-tooltip"
          data-tooltip-content={!isCompanyActive ? "Company is inactive" : "Cancel session"}
          className="inline-block"
        >
          <button
            onClick={() => {
              if (!isCompanyActive) return;
              openConfirmationModal(
                'Cancel Session?',
                'Are you sure you want to cancel this session?',
                () => handleAssignStatus(false)
              );
            }}
            disabled={!isCompanyActive}
            className={`text-red-600 ${!isCompanyActive ? 'opacity-50 cursor-not-allowed' : 'hover:text-red-700'} transition-colors`}
            aria-label="Cancel session"
          >
            <CrossIcon />
          </button>
        </div>
        <Tooltip id="status-tooltip" place="top" className="!bg-gray-900 !text-white !text-xs !rounded-lg" /> */}

        {/* Complete Session */}
        <div className="relative group inline-block">
          <button
            onClick={() => {
              if (!isCompanyActive) return;
              openConfirmationModal(
                'Complete Session?',
                'Are you sure you want to mark this session as completed?',
                () => handleAssignStatus(true)
              );
            }}
            disabled={!isCompanyActive}
            className={`text-green-600 ${!isCompanyActive
              ? 'opacity-50 cursor-not-allowed'
              : 'hover:text-green-700'
              } transition-colors`}
            aria-label="Complete session"
          >
            <CheckIcon />
          </button>

          {/* Tooltip */}
          <div className="absolute right-full mr-2 top-1/2 -translate-y-1/2 opacity-0 group-hover:opacity-100 transition pointer-events-none z-10">
            <div className="bg-white text-black text-sm px-3 py-2 rounded shadow-[0px_8px_16px_-2px_rgba(27,33,44,0.12)] whitespace-nowrap">
              {!isCompanyActive ? "Company is inactive" : "Complete session"}
            </div>
            <div className="w-2 h-2 bg-white rotate-45 absolute left-full top-1/2 -translate-y-1/2 -ml-1"></div>
          </div>
        </div>

        {/* Cancel Session */}
        <div className="relative group inline-block">
          <button
            onClick={() => {
              if (!isCompanyActive) return;
              openConfirmationModal(
                'Cancel Session?',
                'Are you sure you want to cancel this session?',
                () => handleAssignStatus(false)
              );
            }}
            disabled={!isCompanyActive}
            className={`text-red-600 ${!isCompanyActive
              ? 'opacity-50 cursor-not-allowed'
              : 'hover:text-red-700'
              } transition-colors`}
            aria-label="Cancel session"
          >
            <CrossIcon />
          </button>

          {/* Tooltip */}
          <div className="absolute right-full mr-2 top-1/2 -translate-y-1/2 opacity-0 group-hover:opacity-100 transition pointer-events-none z-10">
            <div className="bg-white text-black text-sm px-3 py-2 rounded shadow-[0px_8px_16px_-2px_rgba(27,33,44,0.12)] whitespace-nowrap">
              {!isCompanyActive ? "Company is inactive" : "Cancel session"}
            </div>
            <div className="w-2 h-2 bg-white rotate-45 absolute left-full top-1/2 -translate-y-1/2 -ml-1"></div>
          </div>
        </div>
      </>
    );
  };

  // ✅ Get file icon based on extension
  const getFileIcon = (url) => {
    const ext = url?.toLowerCase() || '';

    const isImage = ['.png', '.jpg', '.jpeg', '.gif'].some(e => ext.includes(e));
    const isPdf = ext.includes('.pdf');

    if (isImage) {
      return (
        <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
          <path
            d="M9.46602 12.666H3.28693C2.88305 12.666 2.68111 12.666 2.5876 12.5862C2.50647 12.5169 2.46341 12.4129 2.47178 12.3065C2.48143 12.1839 2.62422 12.0411 2.90981 11.7556L8.57844 6.08693C8.84244 5.82292 8.97445 5.69091 9.12667 5.64145C9.26057 5.59795 9.4048 5.59795 9.53869 5.64145C9.69091 5.69091 9.82292 5.82292 10.0869 6.08693L12.666 8.66602V9.46602M9.46602 12.666C10.5861 12.666 11.1462 12.666 11.574 12.448C11.9503 12.2563 12.2563 11.9503 12.448 11.574C12.666 11.1462 12.666 10.5861 12.666 9.46602M9.46602 12.666H3.86602C2.74591 12.666 2.18586 12.666 1.75803 12.448C1.38171 12.2563 1.07575 11.9503 0.884003 11.574C0.666016 11.1462 0.666016 10.5861 0.666016 9.46602V3.86602C0.666016 2.74591 0.666016 2.18586 0.884003 1.75803C1.07575 1.38171 1.38171 1.07575 1.75803 0.884003C2.18586 0.666016 2.74591 0.666016 3.86602 0.666016H9.46602C10.5861 0.666016 11.1462 0.666016 11.574 0.884003C11.9503 1.07575 12.2563 1.38171 12.448 1.75803C12.666 2.18586 12.666 2.74591 12.666 3.86602V9.46602M5.66602 4.33268C5.66602 5.06906 5.06906 5.66602 4.33268 5.66602C3.5963 5.66602 2.99935 5.06906 2.99935 4.33268C2.99935 3.5963 3.5963 2.99935 4.33268 2.99935C5.06906 2.99935 5.66602 3.5963 5.66602 4.33268Z"
            stroke="white"
            strokeWidth="1.33333"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
      );
    }

    if (isPdf) {
      return (
        <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
          <path d="M11.6424 10.2861C11.4593 10.34 11.1905 10.3461 10.9023 10.3044C10.593 10.2596 10.2775 10.1652 9.96737 10.0261C10.5204 9.9458 10.9494 9.97055 11.3163 10.1003C11.4032 10.1311 11.546 10.2133 11.6424 10.2861ZM8.55703 9.77971C8.53453 9.78582 8.51237 9.7916 8.49032 9.79771C8.34152 9.83814 8.19675 9.87759 8.05723 9.91269L7.8691 9.96031C7.49068 10.0559 7.10376 10.1536 6.72161 10.2698C6.86684 9.92023 7.00177 9.56675 7.13388 9.22115C7.2317 8.96532 7.33159 8.70399 7.43489 8.44602C7.4873 8.53242 7.54199 8.61886 7.5989 8.70552C7.85817 9.09972 8.18409 9.46419 8.55703 9.77971ZM7.59443 5.83722C7.61896 6.26863 7.52572 6.68364 7.38895 7.08177C7.22047 6.58947 7.14196 6.04584 7.35259 5.60694C7.40661 5.49444 7.45086 5.43429 7.47956 5.4029C7.52388 5.47122 7.58218 5.62407 7.59443 5.83722ZM5.6188 11.302C5.52414 11.4711 5.42752 11.6293 5.32849 11.7788C5.0895 12.1383 4.69864 12.5233 4.49785 12.5233C4.47809 12.5233 4.45418 12.5201 4.41924 12.4833C4.39675 12.4597 4.39314 12.4429 4.39423 12.4198C4.401 12.2876 4.57645 12.0522 4.83062 11.8339C5.06131 11.6359 5.32206 11.4598 5.6188 11.302ZM12.2817 10.3048C12.2511 9.86446 11.5085 9.58197 11.5012 9.57935C11.2142 9.47776 10.9024 9.42841 10.548 9.42841C10.1686 9.42841 9.7596 9.4832 9.23433 9.60571C8.76694 9.27496 8.3632 8.86089 8.06155 8.40249C7.92834 8.19995 7.80859 7.99779 7.7042 7.80031C7.95904 7.19203 8.18852 6.53799 8.14682 5.80546C8.1132 5.2181 7.84788 4.82356 7.48707 4.82356C7.23955 4.82356 7.02645 5.00656 6.85316 5.36799C6.54404 6.01201 6.62529 6.83607 7.09455 7.8194C6.92555 8.21566 6.76853 8.62648 6.61654 9.02416C6.42744 9.51867 6.23259 10.0289 6.01298 10.5142C5.39712 10.7575 4.89118 11.0525 4.46953 11.4146C4.1933 11.6515 3.8603 12.0135 3.84129 12.3915C3.83201 12.5695 3.89316 12.7328 4.0174 12.8636C4.1494 13.0024 4.31523 13.0756 4.49758 13.0758C5.09982 13.0758 5.67944 12.2498 5.7894 12.0841C6.01069 11.7511 6.21783 11.3797 6.42079 10.9512C6.93197 10.7668 7.47674 10.6291 8.00475 10.4961L8.19385 10.4481C8.336 10.412 8.48374 10.3721 8.63528 10.3307C8.79564 10.2875 8.96063 10.2426 9.12832 10.1999C9.67049 10.5442 10.2535 10.7687 10.8222 10.8511C11.3011 10.9206 11.7265 10.8803 12.0144 10.7304C12.2735 10.5957 12.2878 10.3878 12.2817 10.3048ZM13.4481 14.0911C13.4481 14.8975 12.7361 14.9472 12.5925 14.9488H3.40675C2.60188 14.9488 2.55341 14.2332 2.55188 14.0911L2.55177 1.90866C2.55177 1.10146 3.26505 1.05252 3.40664 1.0509H9.61178L9.61508 1.05418V3.47149C9.61508 3.95661 9.90888 4.87509 11.0215 4.87509H13.4274L13.448 4.89567L13.4481 14.0911ZM12.8777 4.3241H11.0219C10.2173 4.3241 10.1686 3.61238 10.1675 3.47156V1.6074L12.8777 4.3241ZM14 14.0911V4.66839L10.1675 0.826633V0.808759H10.1492L9.84138 0.5H3.40677C2.92017 0.5 2 0.794501 2 1.90892V14.0913C2 14.5785 2.29412 15.5 3.40677 15.5H12.5933C13.0798 15.4999 14 15.2054 14 14.0911Z" fill="white" />
        </svg>
      );
    }

    return (
      <svg width="14" height="16" viewBox="0 0 14 16" fill="none">
        <path
          fillRule="evenodd"
          clipRule="evenodd"
          d="M5.15783 0C4.96241 0 4.77499 0.0766254 4.63681 0.213011L0.215811 4.57665C0.0776328 4.71304 0 4.89802 0 5.09091V13.8182C0 15.0232 0.989677 16 2.2105 16H11.0525C12.2733 16 13.263 15.0232 13.263 13.8182V2.18182C13.263 0.976836 12.2733 0 11.0525 0H5.15783Z"
          fill="white"
        />
      </svg>
    );
  };

  // ✅ Get filename from URL
  const getFileName = (url) => {
    if (!url) return 'Document';
    const fileName = url.split('/').pop();
    return fileName.replace(/^\d+-/, '');
  };
    // Helper function to extract filename from URL
  const getFilenameFromUrl = (url) => {
    if (!url) return 'Unknown file';
    const parts = url.split('/');
    const filename = parts[parts.length - 1];
    const cleanFilename = filename.replace(/^\d+-\d+-/, '');
    return cleanFilename;
  };

 // Helper function to handle document download
  const handleDownload = (url, filename) => {
    const link = document.createElement('a');
    link.href = url;
    link.download = filename;
    link.target = '_blank';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };
  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <h1 className="font-lato font-semibold text-[28px] leading-[150%] text-[#101828]">
          {loading ? 'Loading...' : sessionDetails?.session_name || 'Session Details'}
        </h1>
        <div className="flex items-center gap-3">
          {!loading && !isSessionCancelled && !isSessionCompleted && (
            <StatusBadge status={sessionDetails?.status} />
          )}
          <div className="flex gap-2 items-center">
            {renderStatusButtons()}
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Left Column */}
        <div className="space-y-6">
          {/* Session Information Card */}
          <ToolManagmentCard>
            <h2 className="font-lato font-semibold text-[16px] leading-[150%] text-[#101828] mb-4 text-start">
              Session Information
            </h2>
            <div className="border-b border-gray-200 mb-4" />

            {loading ? (
              <CardLoader />
            ) : error ? (
              <div className="py-8 text-center text-red-600">{error}</div>
            ) : !sessionDetails ? (
              <div className="py-8 text-center text-gray-500">No session details found</div>
            ) : (
              <div className="space-y-4 text-start">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="font-medium text-[14px] leading-[150%] text-[#98A2B3]">Session Name</label>
                    <p className="font-medium text-[16px] leading-[150%] text-[#101828]">{sessionDetails.session_name || 'N/A'}</p>
                  </div>
                  <div>
                    <label className="font-medium text-[14px] leading-[150%] text-[#98A2B3]">Session Date</label>
                    <p className="font-medium text-[16px] leading-[150%] text-[#101828]">{formatDate(sessionDetails.session_date)}</p>
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  {/* <div>
                    <label className="font-medium text-[14px] leading-[150%] text-[#98A2B3]">Session Time</label>
                    <p className="font-medium text-[16px] leading-[150%] text-[#101828]">
                      {formatTime(sessionDetails.session_time)}
                    </p>
                  </div> */}
                  <div>
                    <label className="font-medium text-[14px] leading-[150%] text-[#98A2B3]">Coach Name</label>
                    <p className="font-medium text-[16px] leading-[150%] text-[#101828]">{sessionDetails.coach_name || 'N/A'}</p>
                  </div>
                   <div>
                    <label className="font-medium text-[14px] leading-[150%] text-[#98A2B3]">Global Session Name</label>
                    <p className="font-medium text-[16px] leading-[150%] text-[#101828]">{sessionDetails.session_title || 'N/A'}</p>
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-4">
                 
                  <div>
                    <label className="font-medium text-[14px] leading-[150%] text-[#98A2B3]">Batch Name</label>
                    <p className="font-medium text-[16px] leading-[150%] text-[#101828]">
                      {sessionDetails.batches?.map(batch => batch.batch_name).join(', ') || 'N/A'}
                    </p>
                  </div>
                   <div>
                    <label className="font-medium text-[14px] leading-[150%] text-[#98A2B3]">Tool Name</label>
                    <p className="font-medium text-[16px] leading-[150%] text-[#101828]">
                      {formatToolName(sessionDetails.tools?.[0]?.tool_name || companySessionDetail?.tool?.tool_name || 'N/A')}
                    </p>
                  </div>
                </div>

                {/* Tool Toggle */}
                <div className="grid grid-cols-2 gap-4">
                 
                  <div>
                    <label className="font-medium text-[14px] leading-[150%] text-[#98A2B3] block">Tool</label>
                    <ToggleWithTooltip
                      id="tool-tooltip"
                      checked={sessionDetails?.tool_enabled || false}
                      onChange={handleToolToggle}
                      disabled={areActionsDisabled || sessionDetails?.tool_enabled}
                      loading={loadingStates.tool}
                      tooltipContent={getToggleTooltip('tool')}
                    />
                  </div>
                </div>
              </div>
            )}
          </ToolManagmentCard>

          {/* Assignments Card */}
          <ToolManagmentCard>
            <div className="flex items-center justify-between mb-4">
              <h2 className="font-lato font-semibold text-[16px] leading-[150%] text-[#101828]">
                Assignments
              </h2>
              <div className="flex items-center gap-2">
                <ToggleWithTooltip
                  id="assignment-tooltip"
                  checked={sessionDetails?.assignment_enabled || false}
                  onChange={handleAssignmentToggle}
                  disabled={loading || areActionsDisabled || sessionDetails?.assignment_enabled}
                  loading={loadingStates.assignment}
                  tooltipContent={getToggleTooltip('assignment')}
                />
              </div>
            </div>
            <div className="border-b border-gray-200 mb-4" />

            {loading ? (
              <CardLoader />
            ) : (
              <div className="bg-white rounded-lg border border-gray-200 max-h-[350px] overflow-auto text-start">
                {sessionDetails?.assignments?.length > 0 ? (
                  <table className="w-full">
                    <thead className="sticky top-0 bg-[#FFF4E5] z-10">
                      <tr>
                        <th className="py-3 px-4 text-left text-sm font-medium text-[#475467]">S No.</th>
                        <th className="py-3 px-4 text-left text-sm font-medium text-[#475467]">Assignment Name</th>
                        <th className="py-3 px-4 text-left text-sm font-medium text-[#475467]">Action</th>
                      </tr>
                    </thead>
                    <tbody>
                      {sessionDetails.assignments.map((assignment, index) => (
                        <tr key={assignment.assignment_id} className="border-t border-gray-200">
                          <td className="py-3 px-4 text-sm text-[#1D2939]">
                            {String(index + 1).padStart(2, '0')}
                          </td>
                          <td className="py-3 px-4 text-sm text-[#1D2939]">
                            {assignment.assignment_name}
                          </td>
                          <td className="py-3 px-4 text-start">
                            <button
                              onClick={() => {
                                setSelectedCandidateAssignment(assignment?.questions || []);
                                setAssignmentName(assignment?.assignment_name || "");
                                setDialogOpenAssignment(true);
                              }}
                              className="hover:opacity-70 transition-opacity inline-flex items-center justify-center"
                              title="View Details"
                            >
                              <ViewIcon />
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                ) : (
                  <div className="py-8 text-center text-gray-500">No assignments available</div>
                )}
              </div>
            )}
          </ToolManagmentCard>
        </div>

        {/* Right Column */}
        <div className="space-y-6">
          {/* Supported Documents Card */}
          <ToolManagmentCard>
            <div className="flex items-center justify-between mb-4">
              <h2 className="font-lato font-semibold text-[16px] leading-[150%] text-[#101828] text-start">
                Supported Documents
              </h2>
              <div className="flex items-center gap-2">
                <ToggleWithTooltip
                  id="docs-tooltip"
                  checked={sessionDetails?.supported_doc_enabled || false}
                  onChange={handleDocsToggle}
                  disabled={
                    loading ||
                    areActionsDisabled ||
                    sessionDetails?.supported_doc_enabled
                  }
                  loading={loadingStates.docs}
                  tooltipContent={getToggleTooltip('docs')}
                />
              </div>
            </div>
            <div className="border-b border-gray-200 mb-4" />

            {loading ? (
              <CardLoader />
            ) : (
              <div className="space-y-4 max-h-[400px] overflow-y-auto pr-2">
                {sessionDetails?.supported_documents?.length > 0 ? (
                  sessionDetails.supported_documents.map((docGroup) => (
                    <div key={docGroup.supported_doc_id} className="space-y-2">
                      <h3 className="font-semibold text-sm text-[#101828] text-start">
                        {docGroup.name}
                      </h3>
                      <div className="grid grid-cols-2 gap-3">
                        {docGroup.documents?.length > 0 ? (
                          docGroup.documents.map((doc ,docIndex) => (
                            <div
                               key={doc.document_id || docIndex}
                          className={`flex items-center gap-3 p-3 border border-gray-200 rounded-lg transition-colors
      ${isAdmin ? "bg-white cursor-pointer hover:bg-gray-50" : " cursor-not-allowed "}
    `}
                          onClick={() => {
                            if (isAdmin) {
                              handleDownload(doc.url, getFilenameFromUrl(doc.url));
                            }
                          }}
                            >
                              <div className="relative w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0">
                                <div className="absolute inset-0 rounded-full bg-[#FFB76B] opacity-30" />
                                <div className="relative w-8 h-8 rounded-full bg-[#FF8E1C] flex items-center justify-center">
                                  {getFileIcon(doc.url)}
                                </div>
                              </div>
                              <span className="flex-1 text-[13px] text-[#475467] truncate text-start">
                                {getFileName(doc.url)}
                              </span>
                            </div>
                          ))
                        ) : (
                          <div className="col-span-2 py-4 text-center text-gray-400 text-sm">
                            No documents available
                          </div>
                        )}
                      </div>
                    </div>
                  ))
                ) : (
                  <div className="py-8 text-center text-gray-500">No supported documents available</div>
                )}
              </div>
            )}
          </ToolManagmentCard>

          {/* Candidates Card */}
          <ToolManagmentCard>
            <div className="flex items-center justify-between mb-4">
              <h2 className="font-lato font-semibold text-[16px] leading-[150%] text-[#101828]">
                Candidates
              </h2>
              <span className="text-sm text-[#98A2B3]">
                Total: {candidates.length}
              </span>
            </div>
            <div className="border-b border-gray-200 mb-4" />

            {candidatesLoading ? (
              <CardLoader />
            ) : candidatesError ? (
              <div className="py-8 text-center text-red-600">{candidatesError}</div>
            ) : (
              <div className="bg-white rounded-lg border border-gray-200 max-h-[350px] overflow-auto text-start">
                {candidates.length > 0 ? (
                  <table className="w-full">
                    <thead className="sticky top-0 bg-[#FFF4E5] z-10">
                      <tr>
                        <th className="py-3 px-4 text-left text-sm font-medium text-[#475467]">S No.</th>
                        <th className="py-3 px-4 text-left text-sm font-medium text-[#475467]">Candidates</th>
                        <th className="py-3 px-4 text-left text-sm font-medium text-[#475467]">Email</th>
                        <th className="py-3 px-4 text-center text-sm font-medium text-[#475467]">Action</th>
                      </tr>
                    </thead>
                    <tbody>
                      {candidates.map((candidate, index) => (
                        <tr key={`${candidate.batch_employee_id}-${candidate.id}`} className="border-t border-gray-200 hover:bg-gray-50">
                          <td className="py-3 px-4 text-sm text-[#1D2939]">
                            {String(index + 1).padStart(2, '0')}
                          </td>
                          <td className="py-3 px-4 text-sm text-[#1D2939]">
                            {candidate.name}
                          </td>
                          <td className="py-3 px-4 text-sm text-[#1D2939]">
                            {candidate.email}
                          </td>
                          <td className="py-3 px-4 text-center">
                            {/* <button
                              onClick={() => {
                                const cid = companyId;
                                const empId = candidate?.id;
                                const batchName = (candidate?.batch_name || '').toString().toLowerCase();
                                if (batchName === 'management') {
                                  dispatch(resetsessionName());
                                  navigate(`/companies/companyview?companyId=${cid}&tab=managementOverview&view=true&empId=${empId}`);
                                } else {
                                  navigate(`/companies/companyview?companyId=${cid}&tab=employees&employeeId=${empId}`);
                                }
                              }}
                              className="hover:opacity-70 transition-opacity inline-flex items-center justify-center"
                              title="View Details"
                            >
                              <ViewIcon />
                            </button> */}
                            <div className="relative group inline-block">
                              <button
                                onClick={() => {
                                  const cid = companyId;
                                  const empId = candidate?.id;
                                  const batchName = (candidate?.batch_name || '').toString().toLowerCase();
                                  if (batchName === 'management') {
                                    dispatch(resetsessionName());
                                    navigate(`/companies/companyview?companyId=${cid}&tab=managementOverview&view=true&empId=${empId}`);
                                  } else {
                                    navigate(`/companies/companyview?companyId=${cid}&tab=employees&employeeId=${empId}`);
                                  }
                                }}
                                className="hover:opacity-70 transition-opacity inline-flex items-center justify-center"
                                aria-label="View Details"
                              >
                                <ViewIcon />
                              </button>

                              {/* Tooltip */}
                              <div className="absolute left-1/2 -translate-x-1/2 -top-10 opacity-0 group-hover:opacity-100 transition pointer-events-none z-10">
                                <div className="bg-white text-black text-sm px-3 py-2 rounded-[8px] shadow-[0px_8px_16px_-2px_rgba(27,33,44,0.12)] whitespace-nowrap">
                                  View Details
                                </div>
                                <div className="w-2 h-2 bg-white rotate-45 absolute left-1/2 -translate-x-1/2 top-full -mt-1"></div>
                              </div>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                ) : (
                  <div className="py-8 text-center text-gray-500">
                    No candidates available for this session
                  </div>
                )}
              </div>
            )}
          </ToolManagmentCard>
        </div>
      </div>

      {/* Candidate Modal */}
      <CandidateToolResult
        dialogOpen={dialogOpen}
        onDialogClose={() => {
          setDialogOpen(false);
          setSelectedCandidate(null);
        }}
        candidateData={selectedCandidate}
      />
      <AssignmentResult
        dialogOpen={dialogOpenAssignment}
        onDialogClose={() => {
          setDialogOpenAssignment(false);
          setSelectedCandidateAssignment(null);
        }}
        assignmentName={assignmentName}
        AssignmentData={selectedCandidateAssignment}
      />

      {/* Generic Confirmation Modal */}
      <ConfirmationModal
        dialogOpen={confirmationModal.isOpen}
        onDialogClose={handleConfirmationNo}
        title={confirmationModal.title}
        message={confirmationModal.message}
        onConfirm={handleConfirmationYes}
        showYesNo={true}
      />
    </div>
  );
};

export default SessionView;